/**
 * Form pieces shared by the Creators dashboard's create and edit modals:
 * field styles, the theme swatches/editor, the report preview summary and the
 * page-image field (upload or https URL).
 */

import React, { useRef, useState } from 'react';
import { Button } from '../ui';
import { uploadCreatorPageImage } from '../../services/creatorDashboardsApi';
import { CreatorPageTheme } from './types';

/**
 * Images must be https — the public report page is served over TLS, so an
 * http image is blocked as mixed content and the hero renders empty. Mirrors
 * IMAGE_URL_SCHEMA in the backend's creatorPagesAdmin route.
 */
export const isHttpsUrl = (s: string): boolean => /^https:\/\/\S+$/i.test(s.trim());

export const fieldStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 11px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-1)',
  fontSize: 13.5,
  boxSizing: 'border-box',
};

export const labelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'var(--fg-4)',
  display: 'block',
  marginBottom: 4,
};

export const ThemeSwatches: React.FC<{ theme: CreatorPageTheme }> = ({ theme }) => (
  <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
    {[theme.primary, theme.secondary, theme.surfaceTint].map((c, i) => (
      <span key={i} title={c} style={{ width: 14, height: 14, borderRadius: 4, background: c, border: '1px solid var(--border-1)', display: 'inline-block' }} />
    ))}
    {theme.isFallback && <span style={{ fontSize: 10.5, color: 'var(--fg-4)' }}>CG fallback</span>}
  </span>
);

/** Four native color pickers + a mini hero strip so the combination is visible. */
export const ThemeEditor: React.FC<{
  theme: CreatorPageTheme;
  onChange: (theme: CreatorPageTheme) => void;
  onReset?: () => void;
  resetLabel?: string;
}> = ({ theme, onChange, onReset, resetLabel }) => {
  const fields: Array<{ key: keyof CreatorPageTheme; label: string }> = [
    { key: 'primary', label: 'Primary' },
    { key: 'secondary', label: 'Secondary' },
    { key: 'surfaceTint', label: 'Surface tint' },
    { key: 'textOnPrimary', label: 'Text on primary' },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {fields.map(({ key, label }) => (
          <label key={key} style={{ fontSize: 11.5, color: 'var(--fg-3)', display: 'flex', flexDirection: 'column', gap: 3 }}>
            {label}
            <input
              type="color"
              value={String(theme[key] ?? '#000000')}
              onChange={(e) => onChange({ ...theme, isFallback: false, [key]: e.target.value })}
              style={{ width: 52, height: 30, padding: 0, border: '1px solid var(--border-1)', borderRadius: 6, cursor: 'pointer', background: 'none' }}
            />
          </label>
        ))}
        {onReset && (
          <Button size="sm" variant="ghost" onClick={onReset}>{resetLabel || 'Reset to extracted'}</Button>
        )}
      </div>
      <div
        style={{
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          border: '1px solid var(--border-1)',
          background: theme.surfaceTint,
          padding: '10px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <span style={{ background: theme.primary, color: theme.textOnPrimary, borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 700 }}>
          Payout $12,345
        </span>
        <span style={{ color: theme.primary, fontWeight: 700, fontSize: 12 }}>CG eCPM $18.40</span>
        <span style={{ background: theme.secondary, color: theme.textOnPrimary, borderRadius: 999, padding: '3px 8px', fontSize: 11, fontWeight: 700 }}>
          3.2× AdSense
        </span>
      </div>
    </div>
  );
};

const money = (n: number): string => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Headline numbers from a report preview — shared by the create and edit
 * modals. Channels with no payout yet are named instead of shown as $0.00:
 * the public page opens them in a welcome view. With AdSense hidden the total
 * is Direct Sold only and a missing AdSense eCPM is expected, not a gap.
 */
export const PreviewStats: React.FC<{ preview: any; includeAdSense: boolean }> = ({ preview, includeAdSense }) => {
  const prePayout: string[] = Array.isArray(preview.prePayoutChannelIds) ? preview.prePayoutChannelIds : [];
  const channelCount: number = Array.isArray(preview.channels) ? preview.channels.length : 0;
  if (prePayout.length > 0 && prePayout.length >= channelCount) {
    return (
      <div>
        <strong>No payouts yet.</strong> The dashboard opens in a welcome view — what the report will show, a link to the
        sample report and the Tentpole Calendar — and fills in automatically after the first full month is finalized.
      </div>
    );
  }
  const t = preview.totals ?? {};
  return (
    <>
      <div>
        {preview.period?.fromMonth} → {preview.period?.toMonth} · {includeAdSense ? 'Total payout' : 'Direct Sold payout'}{' '}
        <strong>${money(t.payoutUsd ?? 0)}</strong>
        {t.dsEcpm != null && <> · CG eCPM <strong>${money(t.dsEcpm)}</strong></>}
        {t.adsenseEcpm != null && <> · AdSense eCPM <strong>${money(t.adsenseEcpm)}</strong></>}
        {t.ecpmMultiplier != null && <> · <strong>{t.ecpmMultiplier}×</strong></>}
      </div>
      {includeAdSense && t.adsenseEcpm == null && (
        <div style={{ color: 'var(--fg-4)', fontSize: 12 }}>
          No AdSense eCPM — the YT ad-performance capture may be missing for these months/channels.
        </div>
      )}
      {prePayout.length > 0 && (
        <div style={{ color: 'var(--fg-4)', fontSize: 12 }}>
          {prePayout.length} of {channelCount} channels: first payout pending — they show a welcome card on the dashboard.
        </div>
      )}
    </>
  );
};

/** Client-side guard so an obviously-too-big file fails instantly (server enforces the same 5MB). */
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = 'image/png,image/jpeg,image/webp,image/gif';

/**
 * Page-image control shared by the create and edit modals: upload a file we
 * host ourselves, or paste an existing https URL. Both paths end in the same
 * string value, so callers only deal with a URL.
 */
export const PageImageField: React.FC<{
  value: string;
  onChange: (url: string) => void;
  onError: (title: string, message?: string) => void;
  /** Auto-resolved art shown when the admin hasn't chosen anything (create preview). */
  fallbackUrl?: string;
  placeholder?: string;
  helper?: React.ReactNode;
}> = ({ value, onChange, onError, fallbackUrl, placeholder, helper }) => {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const shown = isHttpsUrl(value) ? value.trim() : fallbackUrl;

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      onError('Image too large', `${(file.size / 1024 / 1024).toFixed(1)}MB — the limit is 5MB.`);
      return;
    }
    setUploading(true);
    try {
      onChange(await uploadCreatorPageImage(file));
    } catch (err: any) {
      onError('Upload failed', err?.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = ''; // re-picking the same file must re-fire onChange
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        {shown && (
          <img
            src={shown}
            alt=""
            referrerPolicy="no-referrer"
            style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', border: '1px solid var(--border-1)', flexShrink: 0 }}
          />
        )}
        <input
          style={{ ...fieldStyle, flex: 1 }}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={uploading}
        />
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPTED_IMAGE_TYPES}
          style={{ display: 'none' }}
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <Button size="sm" variant="subtle" onClick={() => fileRef.current?.click()} disabled={uploading}>
          {uploading ? 'Uploading…' : '⬆ Upload'}
        </Button>
        {value.trim() !== '' && !uploading && (
          <Button size="sm" variant="ghost" onClick={() => onChange('')}>Clear</Button>
        )}
      </div>
      {helper}
    </div>
  );
};
